import {
  useState,
  useMemo,
  useEffect,
  useCallback,
  KeyboardEvent,
  useRef,
} from 'react';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  useSortable,
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Dialog as MUIDialog, IconButton } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import type {
  BaseRequestItemsSchemaDependencyObject,
  DynamicSchema,
  DocumentSchema,
  RequestItemsSchema,
  TaxReturnSummary,
  PaymentEstimateObject,
  ScheduleK1Schema,
} from '@liscio/api';
import {
  RequestSchemaStatus,
  useGetApiV5FirmsRequestsRequestIdSections,
} from '@liscio/api';
import {
  TaxKeyAmountCard,
  TaxDetailsTable,
  ScheduleK1Table,
  PersonalProfileInfo,
  BusinessProfileInfo,
  PDFViewer,
  WorkflowItemChip,
  RichTextHtmlContent,
  hasVisibleHtmlContent,
  getFieldDisplayParts,
  getFirmAuthoredTextBody,
  isEncodedCustomFieldPrompt,
  isFirmAuthoredTextAcknowledgeStep,
  useApiClient,
  useRequestStore,
  useSignedUrl,
} from '@liscio/common';
import {
  Box,
  Button,
  ConfirmationDialog,
  GreenInfoCheckAnimation,
  Grid,
  Icon,
  Skeleton,
  Stack,
  Stepper,
  Tooltip,
  Typography,
  ProgressBarWithLabel,
  styled,
  useTheme,
} from '@liscio/ui';

import { AddCustomItemDialog } from './AddCustomItemDialog';
import { selectClientPreviewTourCandidates } from './clientPreviewTourCandidates';
import type {
  ClientPreviewTourCandidate,
  ClientPreviewTourNavigateSignal,
} from './clientPreviewTourCandidates';
import {
  createGuidedCustomStepInitialFields,
  GUIDED_CUSTOM_STEP_LABEL,
  GUIDED_CUSTOM_STEP_SAMPLE_FILE_HREF,
  GUIDED_CUSTOM_STEP_SAMPLE_FILE_NAME,
  type GuidedCustomStepCommand,
  type GuidedCustomStepCommandSignal,
} from './customStepGuidedDemo';
import { previewServerItemsSyncKey } from './previewServerItemsSync';
import {
  useCreateCustomItem,
  type CustomFieldDefinition,
} from 'modules/requests/hooks/useCreateCustomItem';
import { useDeleteItem } from 'modules/requests/hooks/useDeleteSection';
import { useGetAllItemsInSections } from 'modules/requests/hooks/useGetAllItemsInSections';
import {
  firmAuthoredEffectiveContentType,
  normalizeRequestSectionKey,
  type FirmAuthoredContentPayload,
  useReorderPreviewItems,
} from 'modules/requests/hooks/useReorderPreviewItems';
import { useUpdateCustomItem } from 'modules/requests/hooks/useUpdateCustomItem';

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Extracts the pathname + search from a URL string.
 * If the URL is absolute, returns just the path portion so that
 * axios uses the configured baseURL and auth interceptors.
 * If already relative, returns as-is.
 */
const getUrlPath = (url: string | undefined): string => {
  if (!url) return '';
  try {
    const urlObj = new URL(url);
    return urlObj.pathname + urlObj.search;
  } catch {
    return url;
  }
};

function requestAllowsFirmClientPreviewItemEdits(
  status: string | null | undefined
): boolean {
  if (status == null || status === '') return false;
  const s = String(status).toLowerCase();
  return s === RequestSchemaStatus.draft || s === 'new_request';
}

/**
 * Horizontal inset (MUI `spacing`) for firm-authored rich HTML in the main preview column.
 * `6.24` = prior `5.2` + 20% (≈49.9px/side at default 8px step). Symmetric; see LISD-2064 doc.
 */
const RICH_TEXT_MAIN_COLUMN_GUTTER_SPACING = 6.24;

function firstNonEmptyString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (trimmed) return trimmed;
  }
  return undefined;
}

// ─── Types ──────────────────────────────────────────────────────────────────

type PreviewItem = {
  id: string;
  prompt: string;
  hint: string | null;
  itemIndex: number;
  stepIndex: number;
  itemType: string;
  dependencyType: string | null;
  dependencyObject: BaseRequestItemsSchemaDependencyObject | null;
  complete: boolean;
  firmAuthoredContent?: FirmAuthoredContentPayload | null;
  isCustomStep?: boolean;
};

type PreviewStep = {
  label: string;
  /** REST request-section UUID; used with GraphQL reorder mapping. */
  sectionUuid: string;
  items: PreviewItem[];
};

function previewItemWorkflowOptions(item: PreviewItem): unknown {
  const o: Record<string, unknown> = {};
  if (item.isCustomStep) o.is_custom_step = true;
  if (item.firmAuthoredContent) {
    const fac = item.firmAuthoredContent;
    o.firm_authored_content = {
      content_type: fac.contentType,
      text_content: fac.textContent,
      contentType: fac.contentType,
      textContent: fac.textContent,
    };
  }
  return Object.keys(o).length ? o : null;
}

function previewItemIsDocumentReview(item: PreviewItem | undefined): boolean {
  if (!item) return false;
  const doc = item.dependencyObject as DocumentSchema | null;
  if (!doc?.retrieval_token) return false;
  if (item.dependencyType === 'document') return true;
  const ct = firmAuthoredEffectiveContentType(item.firmAuthoredContent ?? null);
  return (
    item.itemType === 'reviewable_document' || ct === 'file' || ct === 'image'
  );
}

function previewItemIsDeletableCustomStep(item: PreviewItem): boolean {
  if (item.isCustomStep === true) return true;
  return isEncodedCustomFieldPrompt(item.prompt);
}

function previewItemIsGuidedDemoCustomStep(item: PreviewItem): boolean {
  return (
    previewItemIsDeletableCustomStep(item) &&
    getFieldDisplayParts(item).label === GUIDED_CUSTOM_STEP_LABEL
  );
}

function previewItemShouldHideDescription(item: PreviewItem): boolean {
  if (item.isCustomStep === true) return true;
  return isEncodedCustomFieldPrompt(item.prompt);
}

/**
 * REST item `dependency.object` for documents does not always duplicate `firm_authored_content.files`;
 * synthesize a file row for edit when we only have the linked document.
 */
function enrichPreviewFirmAuthoredWithRestDocument(
  fac: FirmAuthoredContentPayload,
  dependencyObject: DocumentSchema | null
): FirmAuthoredContentPayload {
  const eff = firmAuthoredEffectiveContentType(fac);
  if (eff !== 'file' && eff !== 'image') return fac;
  if (fac.files?.length) return fac;
  if (!dependencyObject?.uuid) return fac;
  return {
    ...fac,
    files: [
      {
        uuid: dependencyObject.uuid,
        name: dependencyObject.filename?.trim() || 'Document',
        content_type: 'application/octet-stream',
      },
    ],
  };
}

function previewItemToCustomFieldDefinition(
  item: PreviewItem
): CustomFieldDefinition {
  const parts = getFieldDisplayParts(item);
  const rawFac = item.firmAuthoredContent ?? null;
  const depDoc =
    item.dependencyType === 'document'
      ? (item.dependencyObject as DocumentSchema | null)
      : null;
  const fac =
    rawFac && depDoc
      ? enrichPreviewFirmAuthoredWithRestDocument(rawFac, depDoc)
      : rawFac;
  const eff = firmAuthoredEffectiveContentType(fac);
  const fieldType: CustomFieldDefinition['fieldType'] =
    item.itemType === 'reviewable_document' || eff === 'file' || eff === 'image'
      ? 'file'
      : 'text';

  const firstFile = fac?.files?.[0];
  let firmFiles: CustomFieldDefinition['firmFiles'];
  if (fieldType === 'file') {
    if (firstFile?.uuid) {
      firmFiles = [
        {
          id: firstFile.id,
          uuid: firstFile.uuid,
          name: firstFile.name || 'Document',
          contentType:
            firstFile.content_type ||
            firstFile.contentType ||
            'application/octet-stream',
        },
      ];
    } else {
      firmFiles = [];
    }
  } else {
    firmFiles = [];
  }

  return {
    id: `edit_${item.id}`,
    label: parts.label,
    description: '',
    fieldType,
    required: parts.required,
    textContent: fac?.textContent ?? '',
    firmFiles,
  };
}

// ─── Styled Components (matching client TaxDeliveryView.styles) ─────────────

const PreviewHeader = styled(Stack)(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(3)}`,
  height: '73px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

const PreviewStepperContainer = styled(Box)(({ theme }) => ({
  height: '120px',
  padding: 0,
  borderBottom: `solid 1px ${theme.palette.divider}`,
  backgroundColor: theme.palette.grey[50],
}));

const MainContentGridContainer = styled(Grid)({
  height: 'calc(var(--liscio-app-viewport-height, 100vh) - 193px)',
});

const MainContentGridItem = styled(Grid)({
  height: 'inherit',
});

const SidebarGridItem = styled(Grid)({
  height: 'calc(var(--liscio-app-viewport-height, 100vh) - 193px)',
});

const SidebarItemWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: '12px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '7px',
  marginBottom: '16px',
  cursor: 'pointer',
  transition: 'background-color 0.15s ease, border-color 0.15s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

// ─── Content sub-components ─────────────────────────────────────────────────

/**
 * Renders filing information (personal/business profile)
 */
const FilingInfoContent = ({ readUrl }: { readUrl: string }) => {
  const apiClient = useApiClient();
  const urlPath = getUrlPath(readUrl);
  const { data, isLoading } = useQuery<TaxReturnSummary>({
    queryKey: ['clientPreview', 'filingProfile', urlPath],
    queryFn: () =>
      apiClient.client
        .get<TaxReturnSummary>(urlPath)
        .then((res: { data: TaxReturnSummary }) => res.data),
    enabled: !!urlPath,
  });

  return (
    <Stack mt={1} ml={2} mr={2} gap={1} display="flex">
      <Typography variant="h3">Filing Information</Typography>
      <Typography color="text.secondary">
        If you find that any of the below information is incorrect, please send
        us a message.
      </Typography>
      <Stack gap={2} mt={4}>
        {isLoading && <Skeleton role="progressbar" height={200} />}
        {data?.tax_return_type === 'personal' && (
          <PersonalProfileInfo taxReturnSummary={data} />
        )}
        {data?.tax_return_type === 'business' && (
          <BusinessProfileInfo taxReturnSummary={data} />
        )}
      </Stack>
    </Stack>
  );
};

/**
 * Renders balances due & refunds (TaxKeyAmountCard list)
 */
const BalancesContent = ({ readUrl }: { readUrl: string }) => {
  const apiClient = useApiClient();
  const urlPath = getUrlPath(readUrl);
  const { data, isLoading } = useQuery<TaxReturnSummary>({
    queryKey: ['clientPreview', 'taxBalances', urlPath],
    queryFn: () =>
      apiClient.client
        .get<TaxReturnSummary>(urlPath)
        .then((res: { data: TaxReturnSummary }) => res.data),
    enabled: !!urlPath,
  });

  return (
    <Stack mt={1} ml={2} mr={2} gap={1} display="flex">
      <Typography variant="h3">Balances Due & Refunds</Typography>
      <Stack gap={2} mt={2}>
        {isLoading && <Skeleton role="progressbar" height={200} />}
        {data?.key_amounts?.map((keyAmount, index) => (
          <TaxKeyAmountCard
            key={index}
            title={keyAmount.label || '-'}
            amountDueInCents={keyAmount.amount_due_in_cents}
            refundInCents={keyAmount.refund_in_cents}
          />
        ))}
      </Stack>
    </Stack>
  );
};

/**
 * Renders estimated payments table
 */
const EstimatesContent = ({ readUrl }: { readUrl: string }) => {
  const apiClient = useApiClient();
  const urlPath = getUrlPath(readUrl);
  const { data, isLoading } = useQuery<PaymentEstimateObject[]>({
    queryKey: ['clientPreview', 'paymentEstimates', urlPath],
    queryFn: () =>
      apiClient.client
        .get<PaymentEstimateObject[]>(urlPath)
        .then((res: { data: PaymentEstimateObject[] }) => res.data),
    enabled: !!urlPath,
  });

  if (isLoading) {
    return <Skeleton role="progressbar" height={200} />;
  }

  return <TaxDetailsTable data={data || []} />;
};

/**
 * Renders Schedule K-1 table
 */
const K1Content = ({ readUrl }: { readUrl: string }) => {
  const apiClient = useApiClient();
  const urlPath = getUrlPath(readUrl);
  const { data, isLoading } = useQuery<ScheduleK1Schema[]>({
    queryKey: ['clientPreview', 'scheduleK1', urlPath],
    queryFn: () =>
      apiClient.client
        .get<ScheduleK1Schema[]>(urlPath)
        .then((res: { data: ScheduleK1Schema[] }) => res.data),
    enabled: !!urlPath,
  });

  return (
    <Stack mt={1} ml={2} mr={2} gap={1} display="flex">
      <Typography variant="h3">K-1 Packages</Typography>
      <Typography color="text.secondary">
        You can download all K-1 documents from your Taxes page after completing
        this request.
      </Typography>
      <ScheduleK1Table
        data={data || []}
        isLoading={isLoading}
        onView={() => {
          // Preview mode — no action
        }}
      />
    </Stack>
  );
};

/**
 * Renders a reviewable document via PDF viewer
 */
const DocumentContent = ({
  retrievalToken,
  introText,
  fileName,
  fileMimeType,
}: {
  retrievalToken: string | null;
  introText?: string;
  fileName?: string | null;
  fileMimeType?: string | null;
}) => {
  const { signedUrlData, setRetrievalToken, refreshSignedUrl, isLoading } =
    useSignedUrl();

  useEffect(() => {
    if (retrievalToken) {
      setRetrievalToken(retrievalToken);
      refreshSignedUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retrievalToken]);

  if (isLoading || !signedUrlData?.signed_url) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100%">
        <Skeleton width="100%" height={400} />
      </Stack>
    );
  }

  const showIntro = hasVisibleHtmlContent(introText);
  const normalizedFileName = String(fileName || '')
    .trim()
    .toLowerCase();
  const normalizedMimeType = String(fileMimeType || '')
    .trim()
    .toLowerCase();
  const isImageDocument =
    normalizedMimeType.startsWith('image/') ||
    ['.png', '.jpg', '.jpeg', '.gif', '.webp'].some((ext) =>
      normalizedFileName.endsWith(ext)
    );

  return (
    <Stack
      height="100%"
      spacing={2}
      sx={{
        px: showIntro ? RICH_TEXT_MAIN_COLUMN_GUTTER_SPACING : 0,
        py: showIntro ? 2 : 0,
      }}
    >
      {showIntro && introText && <RichTextHtmlContent html={introText} />}
      <Box flex={1} minHeight={0}>
        {isImageDocument ? (
          <Box
            component="img"
            src={signedUrlData.signed_url}
            alt={fileName || 'Uploaded image preview'}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'block',
              borderRadius: 1,
            }}
          />
        ) : (
          <PDFViewer url={signedUrlData.signed_url} height="100%" />
        )}
      </Box>
    </Stack>
  );
};

/**
 * Placeholder content for e-sign items
 */
const ESignPlaceholder = () => (
  <Stack
    alignItems="center"
    justifyContent="center"
    height="100%"
    gap={2}
    p={4}
  >
    <Icon icon="edit" sx={{ fontSize: 64, color: 'text.secondary' }} />
    <Typography variant="h3" textAlign="center">
      E-Signature
    </Typography>
    <Typography variant="body1" color="text.secondary" textAlign="center">
      The client will be prompted to sign the document electronically here.
    </Typography>
  </Stack>
);

/**
 * Placeholder content for payable items
 */
const PayablePlaceholder = () => (
  <Stack
    alignItems="center"
    justifyContent="center"
    height="100%"
    gap={2}
    p={4}
  >
    <Icon icon="billing" sx={{ fontSize: 64, color: 'text.secondary' }} />
    <Typography variant="h3" textAlign="center">
      Payment
    </Typography>
    <Typography variant="body1" color="text.secondary" textAlign="center">
      The client will be prompted to make a payment here.
    </Typography>
  </Stack>
);

/**
 * Matches client Tax Delivery CustomItemContent: description is shown only in the
 * label tooltip (not duplicated below); tooltip preserves newlines.
 */
const CustomPreviewLabelBlock = ({
  label,
  description,
}: {
  label: string;
  description: string;
}) => {
  const descriptionTrimmed = description.trim();
  const hasDescription = Boolean(descriptionTrimmed);

  if (!hasDescription) {
    return (
      <Box
        component="span"
        display="inline-block"
        sx={{
          width: 'max-content',
          maxWidth: '100%',
        }}
      >
        <Typography variant="h5" component="span">
          {label}
        </Typography>
      </Box>
    );
  }

  return (
    <Tooltip
      title={descriptionTrimmed}
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            maxWidth: 400,
            whiteSpace: 'pre-wrap',
          },
        },
      }}
    >
      <Box
        component="span"
        display="inline-block"
        tabIndex={0}
        sx={{
          width: 'max-content',
          maxWidth: '100%',
          cursor: 'help',
          borderBottom: '1px dotted',
          borderColor: 'text.secondary',
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 2,
          },
        }}
      >
        <Typography variant="h5" component="span">
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

// ─── Active Item Content Renderer ───────────────────────────────────────────

const ActiveItemContent = ({ item }: { item: PreviewItem | undefined }) => {
  if (!item) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100%">
        <Typography variant="body1" color="text.secondary">
          Select an item from the sidebar to preview.
        </Typography>
      </Stack>
    );
  }

  const dynamicObj = item.dependencyObject as DynamicSchema | null;
  const docObj = item.dependencyObject as DocumentSchema | null;
  const fac = item.firmAuthoredContent;
  const opts = previewItemWorkflowOptions(item);
  const hideDescription = previewItemShouldHideDescription(item);
  const customFileIntroText = (() => {
    if (!item.isCustomStep) return '';
    if (firmAuthoredEffectiveContentType(fac ?? null) !== 'file') return '';
    return getFirmAuthoredTextBody(opts);
  })();
  const customFileRef = fac?.files?.[0];
  const documentFileName =
    firstNonEmptyString(
      docObj?.filename,
      customFileRef?.name
    ) || null;
  const dependencyMimeType = firstNonEmptyString(
    (docObj as Record<string, unknown> | null)?.['aws_url_content_type'],
    (docObj as Record<string, unknown> | null)?.['content_type'],
    (docObj as Record<string, unknown> | null)?.['contentType'],
    (docObj as Record<string, unknown> | null)?.['mime_type'],
    (docObj as Record<string, unknown> | null)?.['mimeType']
  );
  const customFileMimeType = firstNonEmptyString(
    customFileRef?.content_type,
    customFileRef?.contentType
  );
  const documentMimeType = firstNonEmptyString(
    customFileMimeType,
    dependencyMimeType
  );

  if (item.dependencyType === 'document' && docObj?.retrieval_token) {
    return (
      <DocumentContent
        retrievalToken={docObj.retrieval_token}
        introText={customFileIntroText}
        fileName={documentFileName}
        fileMimeType={documentMimeType}
      />
    );
  }

  // Dynamic items — route by sub-type
  if (item.itemType === 'dynamic' && dynamicObj?.read_url) {
    const dynamicType = dynamicObj.type;

    if (dynamicType === 'filing_profile') {
      return <FilingInfoContent readUrl={dynamicObj.read_url} />;
    }
    if (dynamicType === 'balances') {
      return <BalancesContent readUrl={dynamicObj.read_url} />;
    }
    if (dynamicType === 'payment_estimates') {
      return <EstimatesContent readUrl={dynamicObj.read_url} />;
    }
    if (dynamicType === 'schedule_k1s') {
      return <K1Content readUrl={dynamicObj.read_url} />;
    }
  }

  if (previewItemIsDocumentReview(item) && docObj?.retrieval_token) {
    return (
      <DocumentContent
        retrievalToken={docObj.retrieval_token}
        introText={customFileIntroText}
        fileName={documentFileName}
        fileMimeType={documentMimeType}
      />
    );
  }

  if (
    isFirmAuthoredTextAcknowledgeStep(opts, item.prompt, item.itemType) &&
    firmAuthoredEffectiveContentType(fac ?? null) === 'text'
  ) {
    const body = getFirmAuthoredTextBody(opts);
    const parts = getFieldDisplayParts(item);
    const showBody = hasVisibleHtmlContent(body);
    return (
      <Stack
        mt={0}
        mx={RICH_TEXT_MAIN_COLUMN_GUTTER_SPACING}
        gap={2}
        height="100%"
        justifyContent="safe center"
        alignItems="stretch"
        sx={{ overflowY: 'auto' }}
      >
        <Box sx={{ alignSelf: 'stretch', maxWidth: '100%', textAlign: 'center' }}>
          <CustomPreviewLabelBlock
            label={parts.label}
            description={hideDescription ? '' : parts.description}
          />
        </Box>
        {parts.required && (
          <Typography variant="caption" color="error">
            * Required
          </Typography>
        )}
        {showBody ? (
          <Box sx={{ width: '100%' }}>
            <RichTextHtmlContent html={body} />
          </Box>
        ) : null}
      </Stack>
    );
  }

  // Signable document — e-sign placeholder
  if (item.itemType === 'signable_document') {
    return <ESignPlaceholder />;
  }

  // Payable — payment placeholder
  if (item.itemType === 'payable') {
    return <PayablePlaceholder />;
  }

  // Custom text item — show text input placeholder
  if (item.itemType === 'text') {
    const parts = getFieldDisplayParts(item);
    return (
      <Stack mt={2} mx={3} gap={2}>
        <Box sx={{ alignSelf: 'flex-start', maxWidth: '100%' }}>
          <CustomPreviewLabelBlock
            label={parts.label}
            description={hideDescription ? '' : parts.description}
          />
        </Box>
        {parts.required && (
          <Typography variant="caption" color="error">
            * Required
          </Typography>
        )}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            minHeight: 100,
            bgcolor: 'grey.50',
          }}
        >
          <Typography variant="body2" color="text.disabled">
            Client will enter their response here…
          </Typography>
        </Box>
      </Stack>
    );
  }

  // Custom file/image upload item — show upload placeholder
  if (item.itemType === 'upload_document') {
    const parts = getFieldDisplayParts(item);
    return (
      <Stack mt={2} mx={3} gap={2}>
        <Box sx={{ alignSelf: 'flex-start', maxWidth: '100%' }}>
          <CustomPreviewLabelBlock
            label={parts.label}
            description={hideDescription ? '' : parts.description}
          />
        </Box>
        {parts.required && (
          <Typography variant="caption" color="error">
            * Required
          </Typography>
        )}
        <Box
          sx={{
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            bgcolor: 'grey.50',
          }}
        >
          <Icon
            icon="upload"
            sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
          />
          <Typography variant="body2" color="text.disabled">
            Client will upload files here…
          </Typography>
        </Box>
      </Stack>
    );
  }

  // Boolean item — show yes/no placeholder
  if (item.itemType === 'boolean') {
    const parts = getFieldDisplayParts(item);
    return (
      <Stack mt={2} mx={3} gap={2}>
        <Box sx={{ alignSelf: 'flex-start', maxWidth: '100%' }}>
          <CustomPreviewLabelBlock
            label={parts.label}
            description={parts.description}
          />
        </Box>
        {parts.required && (
          <Typography variant="caption" color="error">
            * Required
          </Typography>
        )}
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" disabled>
            Yes
          </Button>
          <Button variant="outlined" disabled>
            No
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack alignItems="center" justifyContent="center" height="100%">
      <Typography variant="body1" color="text.secondary">
        No preview available for this item.
      </Typography>
    </Stack>
  );
};

// ─── Sortable Sidebar Item ──────────────────────────────────────────────────

const POINTER_SENSOR_CONFIG = { activationConstraint: { distance: 8 } };

const SortableSidebarItem = ({
  item,
  index,
  isActive,
  tourTargetId,
  onClick,
  onKeyDown,
  disableDrag = false,
  onDelete,
  onEdit,
}: {
  item: PreviewItem;
  index: number;
  isActive: boolean;
  tourTargetId?: string;
  onClick: () => void;
  onKeyDown: (e: KeyboardEvent) => void;
  disableDrag?: boolean;
  onDelete?: (item: PreviewItem) => void;
  onEdit?: (item: PreviewItem) => void;
}) => {
  const theme = useTheme();
  const display = getFieldDisplayParts(item);
  const isGuidedDemoCustomStep =
    previewItemIsDeletableCustomStep(item) && display.label === GUIDED_CUSTOM_STEP_LABEL;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: disableDrag });

  const style = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    }),
    [transform, transition, isDragging]
  );

  return (
    <SidebarItemWrapper
      ref={setNodeRef}
      data-tour-id={
        tourTargetId ??
        (isGuidedDemoCustomStep ? 'tax-delivery-custom-step-created' : undefined)
      }
      style={style}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role="button"
      tabIndex={0}
      sx={{
        color: isActive
          ? theme.palette.text.primary
          : theme.palette.text.secondary,
        borderColor: isActive
          ? theme.palette.primary.main
          : theme.palette.divider,
        backgroundColor: isActive
          ? theme.palette.action.selected
          : 'transparent',
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ width: '100%', minWidth: 0 }}
      >
        {!disableDrag && (
          <IconButton
            size="small"
            {...attributes}
            {...listeners}
            sx={{
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' },
              p: 0,
              flexShrink: 0,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Icon
              icon="ellipsisVertical"
              sx={{ fontSize: 18, color: 'text.primary' }}
            />
          </IconButton>
        )}
        <Stack
          direction="row"
          alignItems="center"
          gap={0.5}
          sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
        >
          <Typography
            variant="body1"
            fontWeight="normal"
            noWrap
            component="span"
          >
            {index + 1}. {display.label}
          </Typography>
          {display.required && (
            <Typography
              variant="caption"
              color="error"
              sx={{ lineHeight: 1, flexShrink: 0 }}
            >
              *
            </Typography>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5} flexShrink={0}>
          {onEdit && previewItemIsDeletableCustomStep(item) && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(item);
              }}
              aria-label="edit custom step"
              sx={{ p: 0.5, flexShrink: 0 }}
            >
              <Icon
                icon="pencil"
                sx={{ fontSize: 18, color: 'text.secondary' }}
              />
            </IconButton>
          )}
          {onDelete && previewItemIsDeletableCustomStep(item) && (
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(item);
              }}
              aria-label="remove item"
              sx={{ p: 0.5, flexShrink: 0 }}
            >
              <Icon icon="delete" sx={{ fontSize: 18, color: 'error.main' }} />
            </IconButton>
          )}
          <WorkflowItemChip isComplete={item.complete} isActive={isActive} />
        </Stack>
      </Stack>
    </SidebarItemWrapper>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────

export type ClientPreviewDialogProps = {
  open: boolean;
  onClose: () => void;
  openCustomStepDialogSignal?: number;
  onGuidedTourClose?: () => void;
  onClientPreviewTourCandidatesChange?: (
    candidates: ClientPreviewTourCandidate[]
  ) => void;
  clientPreviewTourNavigateSignal?: ClientPreviewTourNavigateSignal | null;
  onGuidedCustomStepSaved?: () => void;
  guidedCustomStepCommandSignal?: GuidedCustomStepCommandSignal | null;
  onGuidedDemoCommandComplete?: (
    command: GuidedCustomStepCommand,
    success?: boolean
  ) => void;
};

export const ClientPreviewDialog = ({
  open,
  onClose,
  openCustomStepDialogSignal = 0,
  onGuidedTourClose,
  onClientPreviewTourCandidatesChange,
  clientPreviewTourNavigateSignal,
  onGuidedCustomStepSaved,
  guidedCustomStepCommandSignal,
  onGuidedDemoCommandComplete,
}: ClientPreviewDialogProps) => {
  const theme = useTheme();
  const borderColor = theme.palette.divider;
  const { requestId, request } = useRequestStore();
  const canEditPreviewItems = requestAllowsFirmClientPreviewItemEdits(
    request?.status
  );

  // ── State ──
  const [activeStep, setActiveStep] = useState(0);
  const [activeItem, setActiveItem] = useState(0);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [showCongratulations, setShowCongratulations] = useState(false);
  const wasOpenRef = useRef(false);
  const handledClientPreviewTourSignalRef = useRef<number | null>(null);
  const handledGuidedCommandSignalRef = useRef<number | null>(null);

  const queriesEnabled = open && Boolean(requestId);

  // ── Drag-and-drop sensors ──
  const sensors = useSensors(useSensor(PointerSensor, POINTER_SENSOR_CONFIG));

  // ── Reorder hook (workflow GraphQL: section mapping + firm-authored metadata) ──
  const {
    firmAuthoredContentByRequestItemUuid,
    firmAuthoredContentByWorkflowItemId,
    isCustomStepByRequestItemUuid,
    isCustomStepByWorkflowItemId,
    getWorkflowSectionMapping,
    reorderItemsForRequestSection,
    workflowId,
  } = useReorderPreviewItems(requestId || '', queriesEnabled);

  const [addCustomItemOpen, setAddCustomItemOpen] = useState(false);
  const [customStepDialogMode, setCustomStepDialogMode] = useState<
    'add' | 'edit'
  >('add');
  const [customStepDialogInitialFields, setCustomStepDialogInitialFields] =
    useState<CustomFieldDefinition[] | undefined>(undefined);
  const [customStepDialogGuidedDemo, setCustomStepDialogGuidedDemo] =
    useState(false);
  const [editCustomStepWorkflowItemId, setEditCustomStepWorkflowItemId] =
    useState<string | null>(null);

  useEffect(() => {
    if (!open || openCustomStepDialogSignal <= 0 || !canEditPreviewItems) {
      return;
    }

    setCustomStepDialogMode('add');
    setCustomStepDialogInitialFields(createGuidedCustomStepInitialFields());
    setCustomStepDialogGuidedDemo(true);
    setEditCustomStepWorkflowItemId(null);
    setAddCustomItemOpen(true);
  }, [canEditPreviewItems, open, openCustomStepDialogSignal]);

  const { createCustomFields, isCreating } = useCreateCustomItem();
  const { updateCustomField, isUpdating } = useUpdateCustomItem();

  // ── Item deletion ──
  const { deleteItem, isDeleting: isDeletingItem } = useDeleteItem();
  const [deleteItemDialogOpen, setDeleteItemDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    workflowItemId: string;
    label: string;
  } | null>(null);

  // ── Fetch section titles for stepper ──
  const { data: sectionsData } = useGetApiV5FirmsRequestsRequestIdSections(
    requestId!,
    { per_page: 100 },
    { query: { enabled: queriesEnabled } }
  );

  // ── Fetch items per section ──
  const { itemsWithSections } = useGetAllItemsInSections(queriesEnabled);

  // ── Build steps from sections ──
  const steps: PreviewStep[] = useMemo(() => {
    if (!sectionsData?.data) return [];

    const sortedSections = [...sectionsData.data].sort(
      (a, b) => a.position - b.position
    );

    return sortedSections.map((section, stepIdx) => {
      const sectionItems =
        itemsWithSections.find((s) => s.sectionId === section.uuid)?.items ||
        [];

      const sortedItems = [...sectionItems].sort(
        (a, b) => a.position - b.position
      );

      const mapping = getWorkflowSectionMapping(section.uuid, stepIdx);
      const wfIds = mapping?.workflowItemIds ?? [];

      return {
        label: section.title,
        sectionUuid: section.uuid,
        items: sortedItems.map((item: RequestItemsSchema, itemIdx: number) => {
          const riKey = normalizeRequestSectionKey(item.uuid);
          let fac = firmAuthoredContentByRequestItemUuid.get(riKey);
          const wfId = wfIds[itemIdx];
          if (fac == null && wfId) {
            fac = firmAuthoredContentByWorkflowItemId.get(
              normalizeRequestSectionKey(wfId)
            );
          }
          const isCustomStep =
            isCustomStepByRequestItemUuid.get(riKey) === true ||
            (wfId != null &&
              isCustomStepByWorkflowItemId.get(
                normalizeRequestSectionKey(wfId)
              ) === true);

          const hintRaw = (item as { hint?: string | null }).hint;

          return {
            id: item.uuid,
            prompt: item.prompt || '',
            hint: hintRaw ?? null,
            itemIndex: itemIdx,
            stepIndex: stepIdx,
            itemType: item.item_type || '',
            dependencyType: item.dependency?.type || null,
            dependencyObject: item.dependency?.object || null,
            complete: false,
            firmAuthoredContent: fac ?? null,
            isCustomStep,
          };
        }),
      };
    });
  }, [
    sectionsData,
    itemsWithSections,
    getWorkflowSectionMapping,
    firmAuthoredContentByRequestItemUuid,
    firmAuthoredContentByWorkflowItemId,
    isCustomStepByRequestItemUuid,
    isCustomStepByWorkflowItemId,
  ]);

  const clientPreviewTourCandidates = useMemo(
    () => selectClientPreviewTourCandidates(steps),
    [steps]
  );
  const clientPreviewSignatureStepIndex = useMemo(
    () =>
      clientPreviewTourCandidates.find((candidate) => candidate.id === 'signature')
        ?.stepIndex,
    [clientPreviewTourCandidates]
  );

  useEffect(() => {
    onClientPreviewTourCandidatesChange?.(
      open ? clientPreviewTourCandidates : []
    );
  }, [clientPreviewTourCandidates, onClientPreviewTourCandidatesChange, open]);

  // ── Map request item UUID → workflow item UUID ──
  // Built from both data sources (REST sections + GraphQL section mappings).
  // This is kept separate from PreviewItem to avoid stale-state issues with
  // the optimistic localItems array.
  const workflowItemIdMap = useMemo(() => {
    const map = new Map<string, string>();
    if (!sectionsData?.data) return map;

    const sorted = [...sectionsData.data].sort(
      (a, b) => a.position - b.position
    );
    sorted.forEach((section, sIdx) => {
      const sItems =
        itemsWithSections.find((s) => s.sectionId === section.uuid)?.items ||
        [];
      const sortedItems = [...sItems].sort((a, b) => a.position - b.position);
      const mapping = getWorkflowSectionMapping(section.uuid, sIdx);
      const wfIds = mapping?.workflowItemIds ?? [];
      sortedItems.forEach((item, iIdx) => {
        if (wfIds[iIdx]) {
          map.set(item.uuid, wfIds[iIdx]);
        }
      });
    });
    return map;
  }, [sectionsData, itemsWithSections, getWorkflowSectionMapping]);

  const currentStep = steps[activeStep];
  const derivedItems = useMemo(() => currentStep?.items ?? [], [currentStep]);

  // ── Local items state for optimistic reorder ──
  const [localItems, setLocalItems] = useState<PreviewItem[]>([]);

  // Sync when server-derived *content* changes (e.g. GraphQL firm-authored maps after REST),
  // not only when length changes — but do NOT depend on `derivedItems` array identity: `steps`
  // useMemo builds new arrays/objects often, so `[derivedItems]` retriggers every render → loop.
  const derivedItemsSyncKey = useMemo(
    () => previewServerItemsSyncKey(activeStep, derivedItems),
    [activeStep, derivedItems]
  );

  useEffect(() => {
    setLocalItems(derivedItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- derivedItems intentionally omitted; derivedItemsSyncKey captures content changes without array-reference churn
  }, [activeStep, derivedItemsSyncKey]);

  const currentItems = localItems;
  const activeItemData = currentItems[activeItem];
  const activeItemIsGuidedDemoCustomStep = Boolean(
    activeItemData && previewItemIsGuidedDemoCustomStep(activeItemData)
  );

  // ── Drag-and-drop item IDs for SortableContext ──
  const sortableItemIds = useMemo(
    () => currentItems.map((item) => item.id),
    [currentItems]
  );

  // ── Handle drag end — reorder items optimistically and persist ──
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      if (!canEditPreviewItems) return;
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = localItems.findIndex((item) => item.id === active.id);
      const newIndex = localItems.findIndex((item) => item.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      // Snapshot for rollback
      const previousItems = localItems;
      const previousActiveItem = activeItem;

      // Optimistic UI update — reorder local items immediately
      const reorderedItems = arrayMove(localItems, oldIndex, newIndex).map(
        (item, idx) => ({ ...item, itemIndex: idx })
      );
      setLocalItems(reorderedItems);

      // Adjust activeItem if it was affected by the reorder
      if (activeItem === oldIndex) {
        setActiveItem(newIndex);
      } else if (oldIndex < activeItem && newIndex >= activeItem) {
        setActiveItem(activeItem - 1);
      } else if (oldIndex > activeItem && newIndex <= activeItem) {
        setActiveItem(activeItem + 1);
      }

      // Persist reorder via GraphQL mutation (workflow-level)
      const sectionUuid = steps[activeStep]?.sectionUuid;
      const mapping = sectionUuid
        ? getWorkflowSectionMapping(sectionUuid, activeStep)
        : undefined;
      if (sectionUuid && mapping?.workflowItemIds?.length) {
        const newWorkflowItemIds = arrayMove(
          mapping.workflowItemIds,
          oldIndex,
          newIndex
        );
        try {
          await reorderItemsForRequestSection(
            sectionUuid,
            newWorkflowItemIds,
            activeStep
          );
        } catch {
          // Rollback to previous state on failure
          toast.error('Failed to save item order. Reverting changes.');
          setLocalItems(previousItems);
          setActiveItem(previousActiveItem);
        }
      }
    },
    [
      canEditPreviewItems,
      localItems,
      steps,
      activeStep,
      getWorkflowSectionMapping,
      reorderItemsForRequestSection,
      activeItem,
    ]
  );

  // ── Reset state when dialog opens ──
  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      handledClientPreviewTourSignalRef.current = null;
      return;
    }

    if (wasOpenRef.current) return;

    wasOpenRef.current = true;
    setActiveStep(0);
    setActiveItem(0);
    setShowCongratulations(false);
    setConfirmDialogOpen(false);
    setSubmitDialogOpen(false);
  }, [open]);

  useEffect(() => {
    if (!open || !clientPreviewTourNavigateSignal) {
      return;
    }

    const { signalId, candidate } = clientPreviewTourNavigateSignal;
    if (handledClientPreviewTourSignalRef.current === signalId) {
      return;
    }

    const candidateStep = steps[candidate.stepIndex];
    if (!candidateStep?.items[candidate.itemIndex]) {
      return;
    }

    handledClientPreviewTourSignalRef.current = signalId;
    setShowCongratulations(false);
    setActiveStep(candidate.stepIndex);
    setActiveItem(candidate.itemIndex);
  }, [clientPreviewTourNavigateSignal, open, steps]);

  const openGuidedCustomStep = useCallback(() => {
    for (let stepIndex = 0; stepIndex < steps.length; stepIndex += 1) {
      const itemIndex = steps[stepIndex].items.findIndex(
        previewItemIsGuidedDemoCustomStep
      );

      if (itemIndex >= 0) {
        setShowCongratulations(false);
        setActiveStep(stepIndex);
        setActiveItem(itemIndex);
        return true;
      }
    }

    return false;
  }, [steps]);

  useEffect(() => {
    if (!open || !guidedCustomStepCommandSignal) {
      return;
    }

    if (guidedCustomStepCommandSignal.command === 'openCreatedStep') {
      if (
        handledGuidedCommandSignalRef.current ===
        guidedCustomStepCommandSignal.signalId
      ) {
        return;
      }
      if (openGuidedCustomStep()) {
        handledGuidedCommandSignalRef.current =
          guidedCustomStepCommandSignal.signalId;
        onGuidedDemoCommandComplete?.('openCreatedStep');
      } else {
        onGuidedDemoCommandComplete?.('openCreatedStep', false);
      }
      return;
    }

    if (
      handledGuidedCommandSignalRef.current ===
      guidedCustomStepCommandSignal.signalId
    ) {
      return;
    }
    handledGuidedCommandSignalRef.current = guidedCustomStepCommandSignal.signalId;
  }, [
    guidedCustomStepCommandSignal,
    onGuidedDemoCommandComplete,
    open,
    openGuidedCustomStep,
  ]);

  // ── Navigation handlers ──
  const handleStepperClick = useCallback((index: number) => {
    setActiveStep(index);
    setActiveItem(0);
  }, []);

  const handleItemClick = useCallback((index: number) => {
    setActiveItem(index);
  }, []);

  const handleItemKeyDown = useCallback(
    (index: number) => (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleItemClick(index);
      }
    },
    [handleItemClick]
  );

  const advanceToNext = useCallback(() => {
    // Find next item in current step
    for (let i = activeItem + 1; i < currentItems.length; i++) {
      setActiveItem(i);
      return;
    }
    // All items in current step — move to next step
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
      setActiveItem(0);
      return;
    }
    // Last item of last step — show congratulations
    setShowCongratulations(true);
  }, [activeItem, activeStep, currentItems.length, steps.length]);

  const handleApproveClick = useCallback(() => {
    // If this is a reviewable document, show the confirmation dialog first
    if (activeItemData?.itemType === 'reviewable_document') {
      setConfirmDialogOpen(true);
      return;
    }
    // If this is a signable document (Sign step), show the submit confirmation
    if (activeItemData?.itemType === 'signable_document') {
      setSubmitDialogOpen(true);
      return;
    }
    advanceToNext();
  }, [activeItemData, advanceToNext]);

  const handleConfirmReview = useCallback(() => {
    setConfirmDialogOpen(false);
    advanceToNext();
  }, [advanceToNext]);

  const handleCancelReview = useCallback(() => {
    setConfirmDialogOpen(false);
  }, []);

  const handleConfirmSubmit = useCallback(() => {
    setSubmitDialogOpen(false);
    advanceToNext();
  }, [advanceToNext]);

  const handleCancelSubmit = useCallback(() => {
    setSubmitDialogOpen(false);
  }, []);

  const closeCustomStepDialog = useCallback(() => {
    setAddCustomItemOpen(false);
    setCustomStepDialogMode('add');
    setCustomStepDialogInitialFields(undefined);
    setCustomStepDialogGuidedDemo(false);
    setEditCustomStepWorkflowItemId(null);
  }, []);

  const handleCustomStepDialogClose = useCallback(() => {
    closeCustomStepDialog();
    onGuidedTourClose?.();
  }, [closeCustomStepDialog, onGuidedTourClose]);

  const handleSaveCustomItem = useCallback(
    async (fields: CustomFieldDefinition[]) => {
      if (customStepDialogMode === 'edit') {
        const wfId = editCustomStepWorkflowItemId;
        const field = fields[0];
        if (!wfId || !field) {
          toast.error('Unable to update this step. Please try again.');
          return;
        }
        const ok = await updateCustomField(wfId, field);
        if (ok) closeCustomStepDialog();
        return;
      }

      const step = steps[activeStep];
      const wasGuidedCustomStepSave = customStepDialogGuidedDemo;
      const mapping = step
        ? getWorkflowSectionMapping(step.sectionUuid, activeStep)
        : undefined;
      if (!mapping) {
        toast.error(
          'Could not identify the current section. Please try again.'
        );
        if (wasGuidedCustomStepSave) {
          onGuidedDemoCommandComplete?.('save', false);
        }
        return;
      }
      if (!workflowId) {
        toast.error('Workflow not loaded yet. Please wait and try again.');
        if (wasGuidedCustomStepSave) {
          onGuidedDemoCommandComplete?.('save', false);
        }
        return;
      }

      try {
        await createCustomFields(workflowId, mapping.workflowSectionId, fields);
        closeCustomStepDialog();
        if (wasGuidedCustomStepSave) {
          onGuidedCustomStepSaved?.();
        }
      } catch {
        toast.error('Unable to save this custom step. Please try again.');
        if (wasGuidedCustomStepSave) {
          onGuidedDemoCommandComplete?.('save', false);
        }
      }
    },
    [
      customStepDialogMode,
      editCustomStepWorkflowItemId,
      updateCustomField,
      closeCustomStepDialog,
      steps,
      activeStep,
      getWorkflowSectionMapping,
      workflowId,
      createCustomFields,
      customStepDialogGuidedDemo,
      onGuidedDemoCommandComplete,
      onGuidedCustomStepSaved,
    ]
  );

  const handleEditCustomStepClick = useCallback(
    (item: PreviewItem) => {
      const workflowItemId = workflowItemIdMap.get(item.id);
      if (!workflowItemId) {
        toast.error('Unable to identify this step for editing.');
        return;
      }
      const field = previewItemToCustomFieldDefinition(item);
      if (field.fieldType === 'file' && !field.firmFiles?.[0]?.uuid) {
        toast.error(
          'This file step is missing attachment details. Try removing and re-adding the step, or contact support.'
        );
        return;
      }
      setCustomStepDialogMode('edit');
      setCustomStepDialogInitialFields([field]);
      setCustomStepDialogGuidedDemo(false);
      setEditCustomStepWorkflowItemId(workflowItemId);
      setAddCustomItemOpen(true);
    },
    [workflowItemIdMap]
  );

  // ── Item deletion handler ──
  const handleDeleteItemClick = useCallback(
    (item: PreviewItem) => {
      const workflowItemId = workflowItemIdMap.get(item.id);
      if (!workflowItemId) {
        toast.error('Unable to identify this item for deletion.');
        return;
      }
      setItemToDelete({
        workflowItemId,
        label: getFieldDisplayParts(item).label,
      });
      setDeleteItemDialogOpen(true);
    },
    [workflowItemIdMap]
  );

  const handleConfirmDeleteItem = useCallback(async () => {
    if (!itemToDelete) return;

    const success = await deleteItem(itemToDelete.workflowItemId);
    if (success) {
      setDeleteItemDialogOpen(false);
      setItemToDelete(null);
      // Reset active item to avoid pointing at a stale index
      setActiveItem(0);
    }
  }, [itemToDelete, deleteItem]);

  // ── Determine action button label ──
  const getActionButtonLabel = () => {
    if (!activeItemData) return 'Next';

    const isReviewableDocument = previewItemIsDocumentReview(activeItemData);
    const isSignableDocument = activeItemData.itemType === 'signable_document';
    const hasMoreItems = activeItem < currentItems.length - 1;

    if (isReviewableDocument) {
      return hasMoreItems ? 'Approve & Next' : 'Approve';
    }
    if (isSignableDocument) {
      return 'Submit';
    }
    return 'Next';
  };

  // ── Progress calculation ──
  const completedCount = 0; // Preview mode — nothing is completed
  const totalCount = currentItems.length;
  const progressValue =
    totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <MUIDialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
        },
      }}
    >
      {/* ── Header (matches client TaxDeliveryDesktopHeader) ── */}
      <PreviewHeader
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" alignItems="center" gap={2}>
          <IconButton onClick={onClose} aria-label="close preview">
            <Icon icon="arrowLeft" />
          </IconButton>
          <Typography variant="h5" fontWeight={600}>
            {request?.title || 'Tax Delivery'}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              backgroundColor: theme.palette.info.light,
              color: theme.palette.info.contrastText,
              padding: '2px 10px',
              borderRadius: '12px',
              fontWeight: 600,
              textTransform: 'uppercase',
            }}
          >
            Client Preview
          </Typography>
        </Stack>
        <IconButton onClick={onClose} aria-label="close">
          <Icon icon="close" />
        </IconButton>
      </PreviewHeader>

      {/* ── Stepper (matches client StepperContainer) ── */}
      <PreviewStepperContainer>
        <Stepper
          activeStep={activeStep}
          steps={
            steps.length > 0
              ? steps.map((s, index) => ({
                  label: s.label,
                  tourTargetId:
                    index === clientPreviewSignatureStepIndex
                      ? 'tax-delivery-client-preview-sign-step'
                      : undefined,
                }))
              : [{ label: '' }]
          }
          onClick={handleStepperClick}
        />
      </PreviewStepperContainer>

      {/* ── Congratulations Screen ── */}
      {showCongratulations ? (
        <Stack flex={1} direction="row" overflow="auto">
          {/* Left side — animation & congratulations message */}
          <Stack
            flex={1}
            alignItems="center"
            justifyContent="center"
            gap={3}
            p={4}
          >
            <Box
              position="relative"
              height="150px"
              width="150px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box
                sx={{
                  backgroundColor: theme.palette.grey[200],
                  borderRadius: '100%',
                  width: '150px',
                  height: '150px',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <GreenInfoCheckAnimation autoplay />
              </Box>
            </Box>
            <Typography variant="h3">Congratulations!</Typography>
            <Typography
              variant="body1"
              textAlign="center"
              color="text.secondary"
            >
              Thank you for completing {request?.title || 'this request'}.
            </Typography>
          </Stack>

          {/* Right side — What's next section */}
          <Stack flex={1} justifyContent="center" gap={3} p={4} sx={{ pr: 6 }}>
            <Typography variant="h4">What&apos;s next?</Typography>
            <Typography variant="body1">
              There&apos;s nothing more for you to do at this time. You can
              access your detailed tax information on the Taxes tab on your
              Account page.
            </Typography>
            <Stack gap={1.5} alignItems="flex-start">
              <Button variant="contained" disabled>
                Go To Taxes
              </Button>
              <Typography variant="body2" color="text.secondary">
                In the live client experience, this button navigates the client
                to their Taxes page. This action is unavailable during Client
                Preview.
              </Typography>
            </Stack>
            <Box>
              <Button variant="outlined" onClick={onClose}>
                Close Client Preview
              </Button>
            </Box>
          </Stack>
        </Stack>
      ) : (
        <>
          {/* ── Two-panel layout (matches client DesktopMainContentGridContainer) ── */}
          <MainContentGridContainer
            container
            data-tour-id="tax-delivery-client-preview"
          >
            {/* Left Panel: Main Content (8/12 cols, like client) */}
            <MainContentGridItem item md={8}>
              <Stack
                direction="column"
                height="100%"
                borderRight={`solid 1px ${borderColor}`}
                justifyContent="flex-start"
              >
                <Box
                  flex={1}
                  overflow="hidden auto"
                  data-tour-id={
                    activeItemIsGuidedDemoCustomStep
                      ? 'tax-delivery-custom-step-preview'
                      : undefined
                  }
                >
                  <ActiveItemContent item={activeItemData} />
                </Box>
              </Stack>
            </MainContentGridItem>

            {/* Right Panel: Sidebar (4/12 cols, like client) */}
            <SidebarGridItem item md={4}>
              <Stack direction="column" height="inherit">
                <Box
                  flex={1}
                  overflow="hidden auto"
                  p={2}
                  data-tour-id="tax-delivery-client-preview-steps"
                >
                  {/* Progress indicator (matches client exactly) */}
                  {currentItems.length > 0 && (
                    <Box mb={2}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={0.5}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {completedCount} of {totalCount} items reviewed
                        </Typography>
                      </Stack>
                      <ProgressBarWithLabel value={progressValue} />
                    </Box>
                  )}

                  {/* Sidebar items — draggable for reordering */}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={sortableItemIds}
                      strategy={verticalListSortingStrategy}
                    >
                      {currentItems.map((item, i) => (
                        <SortableSidebarItem
                          key={item.id}
                          item={item}
                          index={i}
                          isActive={i === activeItem}
                          tourTargetId={
                            clientPreviewTourCandidates.find(
                              (candidate) =>
                                candidate.stepIndex === activeStep &&
                                candidate.itemIndex === i
                            )?.targetId
                          }
                          onClick={() => handleItemClick(i)}
                          onKeyDown={handleItemKeyDown(i)}
                          disableDrag={!canEditPreviewItems}
                          onDelete={
                            canEditPreviewItems
                              ? handleDeleteItemClick
                              : undefined
                          }
                          onEdit={
                            canEditPreviewItems
                              ? handleEditCustomStepClick
                              : undefined
                          }
                        />
                      ))}
                    </SortableContext>
                  </DndContext>

                  {canEditPreviewItems && (
                    <Button
                      data-tour-id="tax-delivery-custom-step"
                      variant="outlined"
                      size="small"
                      startIcon={<Icon icon="add" />}
                      onClick={() => {
                        setCustomStepDialogMode('add');
                        setCustomStepDialogInitialFields(undefined);
                        setCustomStepDialogGuidedDemo(false);
                        setEditCustomStepWorkflowItemId(null);
                        setAddCustomItemOpen(true);
                      }}
                      sx={{ mt: 1 }}
                      fullWidth
                    >
                      Add custom step
                    </Button>
                  )}
                </Box>

                {/* Action button area (matches client layout) */}
                <Box flex="0" p={2} borderTop={`solid 1px ${borderColor}`}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleApproveClick}
                  >
                    {getActionButtonLabel()}
                  </Button>
                </Box>
              </Stack>
            </SidebarGridItem>
          </MainContentGridContainer>
        </>
      )}

      {/* ── Confirm Document Review Dialog ── */}
      <ConfirmationDialog
        open={confirmDialogOpen}
        onConfirm={handleConfirmReview}
        onCancel={handleCancelReview}
        title="Confirm Document Review"
        content="I have reviewed this document in detail and confirm its accuracy to the best of my knowledge."
        confirmLabel="Agree"
        cancelLabel="Cancel"
      />

      {/* ── Confirm Submission Dialog ── */}
      <ConfirmationDialog
        open={submitDialogOpen}
        onConfirm={handleConfirmSubmit}
        onCancel={handleCancelSubmit}
        title="Confirm Submission"
        content="Are you sure you want to submit your response? You will not be able to make further edits after submission."
        confirmLabel="Submit"
        cancelLabel="Cancel"
      />

      <AddCustomItemDialog
        open={addCustomItemOpen}
        onClose={handleCustomStepDialogClose}
        onSave={handleSaveCustomItem}
        isSaving={isCreating || isUpdating}
        mode={customStepDialogMode}
        initialFieldsForAdd={
          customStepDialogMode === 'add' ? customStepDialogInitialFields : undefined
        }
        initialFieldsForEdit={
          customStepDialogMode === 'edit'
            ? customStepDialogInitialFields
            : undefined
        }
        guidedDemo={customStepDialogGuidedDemo}
        sampleFileHref={
          customStepDialogGuidedDemo
            ? GUIDED_CUSTOM_STEP_SAMPLE_FILE_HREF
            : undefined
        }
        sampleFileName={
          customStepDialogGuidedDemo
            ? GUIDED_CUSTOM_STEP_SAMPLE_FILE_NAME
            : undefined
        }
        guidedDemoCommandSignal={guidedCustomStepCommandSignal}
        onGuidedDemoCommandComplete={onGuidedDemoCommandComplete}
      />

      {/* ── Confirm Item Deletion Dialog ── */}
      <ConfirmationDialog
        open={deleteItemDialogOpen}
        onConfirm={handleConfirmDeleteItem}
        onCancel={() => {
          setDeleteItemDialogOpen(false);
          setItemToDelete(null);
        }}
        title="Remove Custom Item"
        content={`Are you sure you want to remove "${
          itemToDelete?.label || 'this item'
        }"? This action cannot be undone.`}
        confirmLabel="Remove"
        cancelLabel="Cancel"
        isLoading={isDeletingItem}
        disabled={isDeletingItem}
        confirmBtnProps={{ color: 'error' }}
      />
    </MUIDialog>
  );
};
